import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import classNames from "classnames";
import AuthorisedContainer from "components/AuthorisedContainer";
import { PermissionSetMap } from "constants/RBACConstants";
import React from "react";
import { UserInfoProjectorImpl } from "types/api/userInfoProjectorImpl";

import ACButton from "ac-components/ac-button/ACButton";
import CloseIcon from "ac-components/ac-icons/CloseIcon";
import GripIcon from "ac-components/ac-icons/GripIcon";

import { ApproversType } from "../../../../../components/form/Approvers";

interface DraggableApproverItemProps {
  id: string | number;
  approverData: number | string;
  index?: number; // Optional for display/debugging purposes
  disabled: boolean;
  onRemove: (id: string | number) => void;
  userDetail?: UserInfoProjectorImpl;
  roleDetail?: ApproversType;
  hasPermissionToUpdate: boolean;
}

/**
 * A draggable approver item component using dnd-kit's useSortable hook.
 * This component encapsulates the drag-and-drop logic for individual approvers
 * in the approval workflow, supporting users, roles, and external email addresses.
 *
 * @param id - Unique identifier for the approver (used by dnd-kit for tracking)
 * @param approverData - The actual approver data (user ID, role ID, or email string)
 * @param index - Position in the list (for display/debugging purposes)
 * @param disabled - Whether drag-and-drop is disabled (permissions or form state)
 * @param onRemove - Callback when the remove button is clicked
 * @param userDetail - User information if approver is an internal user
 * @param roleDetail - Role information if approver is a role
 * @param hasPermissionToUpdate - Whether the current user has permission to modify approvers
 */
const DraggableApproverItem: React.FC<DraggableApproverItemProps> = ({
  id,
  approverData,
  disabled,
  onRemove,
  userDetail,
  roleDetail,
  hasPermissionToUpdate,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id, disabled });

  // Apply transform styles for smooth drag animation
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Determine if approver is an external email
  const isEmail =
    typeof approverData === "string" && (approverData as string).includes("@");

  // Get display name based on approver type
  const displayName = isEmail
    ? approverData
    : userDetail?.displayName || roleDetail?.label;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className="draggable-approver-container"
      key={id}
    >
      <div
        {...listeners}
        style={{
          fontSize: 18,
          alignSelf: "center",
          cursor: disabled ? "not-allowed" : "grab",
          marginLeft: 4,
        }}
      >
        <GripIcon />
      </div>
      <div className="draggable-approver-name">
        <div className="text-ellipsis" title={displayName as string}>
          {displayName}
        </div>

        <AuthorisedContainer
          permission={PermissionSetMap.MODIFY_APPROVERS_LIST}
        >
          <ACButton type="outline" size="sm" iconOnly>
            <CloseIcon
              onClick={() =>
                hasPermissionToUpdate ? onRemove(approverData) : () => {}
              }
              className={classNames("pull-right", {
                disabled: !hasPermissionToUpdate,
              })}
            />
          </ACButton>
        </AuthorisedContainer>
      </div>
    </div>
  );
};

export default DraggableApproverItem;
